import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import { IMAGES, TESTIMONIALS, STATS } from "../mock";
import { postsApi } from "../api";
import { Reveal, Counter, NewsCard } from "../components/Shared";
import { MarqueeStrip } from "../components/Effects";
import Seo from "../components/Seo";

export default function Home() {
  const [posts, setPosts] = useState([]);
  const [offsetY, setOffsetY] = useState(0);
  const videoRef = useRef(null);

  const slowVideo = useCallback(() => {
    if (videoRef.current) videoRef.current.playbackRate = 0.5;
  }, []);

  useEffect(() => {
    slowVideo();
  }, [slowVideo]);

  useEffect(() => {
    postsApi
      .list({ limit: 3 })
      .then((data) => setPosts(data.slice(0, 3)))
      .catch(() => setPosts([]));
  }, []);

  useEffect(() => {
    const onScroll = () => setOffsetY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        description="Global Equine Group is a unique racing and bloodstock management company based in Newmarket, England. Bespoke, transparent racehorse management for clients across 6 continents."
        image={IMAGES.heroHome}
      />
      {/* ---------- HERO ---------- */}
      <section className="gee-hero flex items-center" style={{ minHeight: "100vh" }}>
        <video
          ref={videoRef}
          className="gee-hero-bg w-full h-full object-cover"
          style={{ transform: `scale(1.05) translateY(${offsetY * 0.15}px)` }}
          poster={IMAGES.heroHome}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onLoadedMetadata={slowVideo}
          onPlay={slowVideo}
          data-testid="hero-video"
        >
          <source src="/hero.webm" type="video/webm" />
        </video>
        <div className="gee-hero-overlay" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 w-full pt-24">
          <Reveal>
            <p className="gee-kicker mb-5 tracking-[0.3em]">Global Equine Group</p>
          </Reveal>
          <Reveal delay={1}>
            <h1 className="font-serif-d text-white text-5xl md:text-7xl leading-[1.08] max-w-3xl">
              A Unique Racing Management Company
            </h1>
          </Reveal>
          <Reveal delay={2}>
            <Link to="/racing-management" className="gee-link gee-link--hero mt-10 inline-flex" data-testid="hero-cta-link">
              Racing &amp; Bloodstock Management <ArrowRight size={17} className="gee-link-arrow" />
            </Link>
          </Reveal>
        </div>
        <div className="gee-hero-notch" />
        <div className="gee-scroll-arrow"><ArrowDown size={20} /></div>
      </section>

      {/* ---------- SECTION 1: Racing & Bloodstock ---------- */}
      <section className="py-24 md:py-32 bg-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-full h-full border-2 border-[#eaf5fc] hidden md:block" />
              <div className="gee-img-frame aspect-square relative">
                <img src={IMAGES.racingCard} alt="Racing and bloodstock management" loading="lazy" />
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="gee-kicker mb-4">Racing &amp; Bloodstock Management</p>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="font-serif-d text-[#0f2438] text-3xl md:text-5xl leading-tight mb-6">
                Bespoke, Unique and Adaptable
              </h2>
              <div className="gee-divider mb-8" />
            </Reveal>
            <Reveal delay={2}>
              <p className="text-[#5b6d7d] leading-relaxed mb-8">
                We aim to maximise the potential of our clients horses through our unique racing
                management structure and services. Our multi-disciplinary team approach brings
                together the expertise, knowledge and skill-sets that allow Global Equine Group to
                produce consistently impressive statistics year after year for our clients. Our
                attention to detail is there to assist the owner, horse, trainer and jockey every
                step of the way.
              </p>
              <Link to="/racing-management" className="gee-link text-[#0f2438]" data-testid="racing-section-link">
                Racing &amp; Bloodstock Management <ArrowRight size={16} className="gee-link-arrow" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- STATS BAND ---------- */}
      <section style={{ backgroundColor: "#0f2438" }} className="py-20 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: `url(${IMAGES.testimonialBg})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i % 4 > 2 ? 3 : i % 4}>
              <div className="text-center" data-testid={`stat-${i}`}>
                <Counter value={s.value} suffix={s.suffix} />
                <p className="text-[#7d94a6] uppercase tracking-[0.18em] text-xs mt-3">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- SECTION 2: Team ---------- */}
      <section className="py-24 md:py-32 bg-[#f7fbfd] overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="order-2 md:order-1">
            <Reveal>
              <p className="gee-kicker mb-4">The Global Equine Group Team</p>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="font-serif-d text-[#0f2438] text-3xl md:text-5xl leading-tight mb-6">
                With You Every Step Of The Way
              </h2>
              <div className="gee-divider mb-8" />
            </Reveal>
            <Reveal delay={2}>
              <p className="text-[#5b6d7d] leading-relaxed mb-8">
                Global Equine Group has extensive experience working for clients globally, and has
                acted on behalf of clients in many countries from Argentina to South Africa; Qatar
                to the USA, managing and providing a consultant service for client's horses. The
                company has built up a network of dependable, trustworthy professionals within the
                equine industry, and knows how to get things done, no matter how big or small the
                challenge.
              </p>
              <Link to="/team" className="gee-link text-[#0f2438]" data-testid="team-section-link">
                The Team <ArrowRight size={16} className="gee-link-arrow" />
              </Link>
            </Reveal>
          </div>
          <Reveal className="order-1 md:order-2">
            <div className="relative">
              <div className="absolute -bottom-6 -right-6 w-full h-full border-2 border-[#dceefa] hidden md:block" />
              <div className="gee-img-frame aspect-square relative">
                <img src={IMAGES.teamCard} alt="The Global Equine Group team" loading="lazy" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- TESTIMONIAL ---------- */}
      <section className="relative py-28 md:py-36 overflow-hidden" style={{ backgroundColor: "#081826" }}>
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url(${IMAGES.testimonialBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transform: `translateY(${(offsetY - 2400) * 0.06}px) scale(1.1)`,
          }}
        />
        <div className="relative max-w-[1000px] mx-auto px-6 lg:px-12 text-center">
          <Reveal>
            <div className="gee-quote-mark select-none" aria-hidden="true">“</div>
          </Reveal>
          <Reveal delay={1}>
            <blockquote className="font-serif-d text-white text-xl md:text-[1.7rem] leading-relaxed italic">
              {TESTIMONIALS[0].quote}
            </blockquote>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-8 text-[#29abe2] font-medium">{TESTIMONIALS[0].name}</p>
            <p className="text-[#7d94a6] text-sm mt-1 uppercase tracking-[0.15em]">
              {TESTIMONIALS[0].role}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------- MARQUEE ---------- */}
      <MarqueeStrip items={["Racing Management", "Bloodstock", "Sales & Purchases", "Veterinary Expertise", "Sports Science", "Global Network"]} />

      {/* ---------- LATEST NEWS ---------- */}
      <section className="py-24 md:py-32 bg-white" data-testid="latest-news-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <Reveal><p className="gee-kicker mb-4">Keep Up To Date</p></Reveal>
              <Reveal delay={1}>
                <h2 className="font-serif-d text-[#0f2438] text-3xl md:text-5xl">Latest News</h2>
              </Reveal>
            </div>
            <Reveal delay={2}>
              <Link to="/blog" className="gee-link text-[#0f2438]" data-testid="view-all-news-link">
                View All <ArrowRight size={16} className="gee-link-arrow" />
              </Link>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {posts.map((post, i) => (
              <NewsCard key={post.id} post={post} delay={i} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
